import { useEffect, useRef, useState } from "react";
import { TestParams, TestTimer } from "../../types/test-types";

const useTestTimer = (initialWords: string[], words: string[], params: TestParams): TestTimer => {
  const TIMER_DELAY = +params.testInterval * 1000;
  const PAUSE_DURATION = +params.testPauseDuration * 1000;

  const INTRODUCTION_SLIDE_DURATION = +params.introductionSlideDuration * 1000;
  const TEST_PROCESS_SLIDE_DURATION = +params.testProcessSlideDuration * 1000;
  const QUESTIONS_SLIDE_DURATION = +params.questionsSlideDuration * 1000;

  const [variables, setVariables] = useState({
    word: "",
    index: -initialWords.length,
    showBlankPage: true,
    paused: false,
    started: false,
    ended: false,
    step: 0,
  });
  const [timerState, setTimerState] = useState({
    trigger: false,
    count: 0,
    delay: 0,
  });

  const interval = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (variables.started && !variables.paused && !variables.ended) {
      interval.current = setInterval(() => {
        setTimerState((map) => {
          if (map.count >= timerState.delay) {
            return { ...map, count: 0, trigger: !map.trigger };
          }
          return { ...map, count: map.count + 10 };
        });
      }, 10);
    } else {
      clearInterval(interval.current);
    }
    return () => {
      clearInterval(interval.current);
    };
  }, [variables.started, timerState.delay, variables.paused, variables.ended]);

  const startTimer = () => {
    setTimerState((map) => ({ ...map, delay: INTRODUCTION_SLIDE_DURATION }));
    setVariables((map) => ({ ...map, started: true }));
  };

  const stopTimer = () => {
    setVariables((map) => ({ ...map, ended: true }));
  };

  const toggleTimer = () => {
    setVariables((map) => ({ ...map, paused: !map.paused }));
  };

  useEffect(() => {
    if (!variables.started) {
      return;
    }
    if (variables.step === 0) {
      handleIntroductionSlide();
    } else if (variables.step === 1) {
      handleTestProcessSlide();
    } else if (variables.step === 2) {
      handleQuestionsSlide();
    } else if (variables.step === 3) {
      showTestFirstWords();
    } else if (variables.step === 4) {
      showTestWords();
    } else if (variables.step === 5) {
      handleMiddlePause();
    } else if (variables.step === 6) {
      showTestWords();
    } else if (variables.step === 7) {
      stopTimer();
    }
  }, [timerState.trigger]);

  const handleIntroductionSlide = () => {
    setTimerState((map) => ({ ...map, delay: TEST_PROCESS_SLIDE_DURATION }));
    setVariables((map) => ({ ...map, step: map.step + 1 }));
  };

  const handleTestProcessSlide = () => {
    setTimerState((map) => ({ ...map, delay: QUESTIONS_SLIDE_DURATION }));
    setVariables((map) => ({ ...map, step: map.step + 1 }));
  };

  const handleQuestionsSlide = () => {
    setTimerState((map) => ({ ...map, delay: TIMER_DELAY }));
    setVariables((map) => ({ ...map, step: map.step + 1 }));
  };

  const showTestFirstWords = () => {
    if (!variables.showBlankPage) {
      if (variables.index >= 0) {
        setVariables((map) => ({ ...map, showBlankPage: true, step: map.step + 1 }));
      } else {
        setVariables((map) => ({ ...map, showBlankPage: true }));
      }
      return;
    }

    setVariables({
      ...variables,
      word: initialWords[variables.index + 2],
      index: variables.index + 1,
      showBlankPage: false,
    });
  };

  const showTestWords = () => {
    if (!variables.showBlankPage) {
      if (variables.index === Math.floor(words.length / 2)) {
        setTimerState((map) => ({ ...map, delay: PAUSE_DURATION }));
        setVariables((map) => ({ ...map, step: map.step + 1 }));
      }

      if (words.length <= variables.index) {
        setVariables((map) => ({ ...map, showBlankPage: true, step: map.step + 1 }));
      } else {
        setVariables((map) => ({ ...map, showBlankPage: true }));
      }
      return;
    }

    setVariables({
      ...variables,
      word: words[variables.index],
      index: variables.index + 1,
      showBlankPage: false,
    });
  };

  const handleMiddlePause = () => {
    setTimerState((map) => ({ ...map, delay: TIMER_DELAY }));
    setVariables((map) => ({ ...map, step: map.step + 1 }));
  };

  return { variables, startTimer, toggleTimer };
};

export default useTestTimer;
